import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { dataURLtoBlob } from '@pages/TwinPage/utils';
import { useCountdown } from 'usehooks-ts';
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner';
import { PhotoCameraIcon } from '../../../../components/Icons';
import {
  useCleanupSelfie,
  useUploadMedia,
  useValidateImage,
} from '../../../../hooks/apiHooks/twinHooks';
import { useTwinPreview } from '../../../../hooks/storeHooks/useTwin';
import { Heading } from '../Heading';
import { ImageValidationError } from './ImageValidationError';

interface CameraUiProps {
  previewUrl: string | ArrayBuffer;
  previewType: 'camera' | 'upload';
  setTabName: (name: string) => void;
}

const Preview: React.FC<CameraUiProps> = memo(
  ({ previewUrl, previewType, setTabName }) => {
    const { t } = useTranslation();
    const { mutateAsync: uploadMedia, isPending: isUploading } =
      useUploadMedia();
    const { mutateAsync: cleanupSelfie, isPending: isCleanupPending } =
      useCleanupSelfie();
    const { mutateAsync: validateImage, isPending: isValidating } =
      useValidateImage();

    const [validationError, setValidationError] = useState<string | null>(null);

    const [improvingCount, { startCountdown }] = useCountdown({
      countStart: 8,
      intervalMs: 1000,
    });

    const {
      previewUrl: imageUrl,
      linkedInImageUrl,
      previewOriginalUrl,
      setPreviewUrl,
      setStyledOriginUrls,
      setPreviewOriginalUrl,
    } = useTwinPreview();

    const handleUpload = async () => {
      try {
        let url = linkedInImageUrl;
        if (previewUrl) {
          let blob: Blob;

          // Handle both data URLs and blob URLs
          if (
            typeof previewUrl === 'string' &&
            previewUrl.toLowerCase().startsWith('data:')
          ) {
            // Data URL - use existing function
            blob = dataURLtoBlob(previewUrl);
          } else if (
            typeof previewUrl === 'string' &&
            previewUrl.toLowerCase().startsWith('blob:')
          ) {
            // Blob URL - fetch the blob
            const response = await fetch(previewUrl);
            blob = await response.blob();
          } else {
            throw new Error('Invalid image format');
          }

          // Create a File object from the blob for validation
          const file = new File([blob], 'selfie.jpeg', { type: 'image/jpeg' });

          const { error_message: errorMessage } = await validateImage(file);

          if (errorMessage) {
            setValidationError(errorMessage);
            return;
          }
          // If validation passes, upload the image
          url =
            (await uploadMedia({
              file: blob,
              type: 'image',
              name: `${Date.now()}-selfie.jpeg`,
            })) || '';
        }

        if (url) {
          setPreviewOriginalUrl(url);
          startCountdown();
          try {
            const { data } = await cleanupSelfie(url);
            setPreviewUrl(data.url);
            setStyledOriginUrls({ original: data.url });
          } catch {
            throw new Error(
              'Temporary error while processing your photo. Please try again.',
            );
          }
        }
      } catch (error) {
        console.error('Error processing image:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to process image';
        setValidationError(errorMessage);
      }
    };

    const onCancel = () => {
      setTabName('');
      setPreviewUrl('');
    };

    const onContinue = () => {
      setTabName('style');
    };

    const onOriginalContinue = () => {
      if (previewOriginalUrl) {
        setPreviewUrl(previewOriginalUrl);
        setStyledOriginUrls({ original: previewOriginalUrl });
      }
      setTabName('style');
    };

    useEffect(() => {
      if (previewUrl || linkedInImageUrl) {
        void handleUpload();
      }
    }, [previewUrl, linkedInImageUrl]);

    // Show validation error if there is one
    if (validationError) {
      return (
        <ImageValidationError
          imageUrl={previewUrl as string}
          errorMessage={validationError}
          onClose={() => {
            setValidationError(null);
            setTabName('');
          }}
        />
      );
    }

    return (
      <div className="w-[750px] max-w-full mx-auto relative overflow-hidden mb-auto sm:mb-0">
        <Heading
          title={t('twin.reviewPhoto')}
          subtitle={t('twin.reviewPhotoSubtitle')}
        />
        <div className="rounded-2xl aspect-video w-[750px] max-w-full mx-auto relative overflow-hidden mb-auto sm:mb-0">
          <img
            src={imageUrl || (previewUrl as string) || linkedInImageUrl || ''}
            alt="Preview"
            className={`w-full h-full object-cover object-center rounded-2xl ${
              previewType === 'camera' ? '-scale-x-100' : ''
            }`}
          />

          {(isValidating || isUploading) && (
            <div className="flex gap-2 items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 whitespace-nowrap tc-mv-blur p-4">
              <AnimatedSpinner />
              {t('twin.uploadingPhoto')}
            </div>
          )}

          {isCleanupPending && (
            <div className="flex gap-2 items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 whitespace-nowrap tc-mv-blur p-4">
              <AnimatedSpinner />
              {improvingCount
                ? t('twin.improvingPhoto')
                : t('twin.almostDone')}
            </div>
          )}
        </div>
        <div className="flex justify-center gap-4 flex-shrink-0 mt-8 w-full">
          <button
            data-testid="preview-change-photo-btn"
            className="btn btn-primary sm:!grow-0 !grow"
            onClick={onCancel}
            disabled={isUploading || isValidating || isCleanupPending}>
            <PhotoCameraIcon size={16} />
            <span className="ml-2">{t('twin.changePhoto')}</span>
          </button>
          <button
            data-testid="preview-continue-btn"
            className="btn btn-primary sm:!grow-0 !grow"
            onClick={onContinue}
            disabled={isUploading || isValidating || isCleanupPending}>
            <span className="ml-2">{t('twin.continue')}</span>
            <i className="fa-solid fa-arrow-right text-lg ml-2" />
          </button>
        </div>
        <button
          data-testid="preview-use-original-btn"
          onClick={onOriginalContinue}
          className="underline mt-2 opacity-50 hover:opacity-100 disabled:opacity-50 text-xs mx-auto block"
          disabled={isUploading || isValidating || isCleanupPending}>
          <span className="ml-2">{t('twin.useOriginalPhoto')}</span>
        </button>
      </div>
    );
  },
);

export { Preview };
